import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import './LanguagesForm.scss';
import { Button, Modal, Select, message } from 'antd';
import { WorkExpDeleteIcon, AddLanguageIcon } from '../../../components/common/CustomIcon';

function LanguagesForm({ service, customClass }) {

    const {
        loadDeveloperLanguageProficiencies,
        saveDeveloperLanguageProficiencies,
        removeDeveloperLanguage,
        onPanelSelect,
        onSaveClick,
        removeEvent,
        getDeveloperLanguageProficiencies
    } = service;

    const {
        proficiencyTypes,
        proficiencyLevels,
        languagesList,
        developerLanguageProficiencies
    } = service.state.languages;

    useEffect(() => {
        onSaveClick('languages', () => handleSave(false));
        return () => removeEvent('languages');
    }, []);

    const setProficiencyForAllProficiencyTypes = (language, proficiencyTypeId, proficiencyLevelId) => {
        // Find the proficiency level name for the selected proficiencyLevelId
        const proficiencyLevelName = proficiencyLevels.find((level) => level.value === proficiencyLevelId)?.label || '';
        // Iterate over all proficiency types and add missing ones with the selected level
        const updatedProficiencies = [...language.proficiencies];

        proficiencyTypes.forEach((type) => {
            const exists = updatedProficiencies.some((proficiency) => proficiency.proficiencyTypeId === type.id);
            if (!exists && type.id !== proficiencyTypeId) {
                updatedProficiencies.push({
                    proficiencyTypeId: type.id,
                    proficiencyTypeName: type.name,
                    proficiencyLevelId,
                    proficiencyLevelName,
                });
            }
        });
        return updatedProficiencies;
    };


    const updateLanguageProficiency = (renderId, proficiencyTypeId, proficiencyLevelId, languageId, languageName) => {
        const updatedDeveloperLanguages = developerLanguageProficiencies.map((language) => {
            if (language.renderId === renderId) {
                // Check if the proficiency exists
                const existingProficiencyIndex = language.proficiencies.findIndex(
                    (proficiency) => proficiency.proficiencyTypeId === proficiencyTypeId
                );

                let updatedProficiencies;

                if (existingProficiencyIndex !== -1) {
                    // If proficiency exists, update both proficiencyLevelId and proficiencyLevelName
                    updatedProficiencies = language.proficiencies.map((proficiency) =>
                        proficiency.proficiencyTypeId === proficiencyTypeId
                            ? {
                                ...proficiency,
                                proficiencyLevelId,
                                proficiencyLevelName:
                                    proficiencyLevels.find((level) => level.value === proficiencyLevelId)?.label || '',
                            }
                            : proficiency
                    );
                } else if (proficiencyTypeId != null && proficiencyLevelId != null) {
                    // If proficiency does not exist and both IDs are valid, add it
                    updatedProficiencies = [
                        ...language.proficiencies,
                        {
                            proficiencyTypeId,
                            proficiencyTypeName: proficiencyTypes.find((type) => type.id === proficiencyTypeId)?.name || '',
                            proficiencyLevelId,
                            proficiencyLevelName: proficiencyLevels.find((level) => level.value === proficiencyLevelId)?.label || '',
                        },
                    ];
                } else {
                    // If proficiencyTypeId or proficiencyLevelId is null/undefined, keep the existing proficiencies unchanged
                    updatedProficiencies = [...language.proficiencies];
                }

                if (proficiencyTypeId != null && proficiencyLevelId != null && language.proficiencies && language.proficiencies.length === 0) {
                    // Use setProficiencyForAllProficiencyTypes to auto-fill missing proficiencies
                    updatedProficiencies = [...updatedProficiencies, ...setProficiencyForAllProficiencyTypes(language, proficiencyTypeId, proficiencyLevelId)]
                }

                // Update language details and proficiencies
                return {
                    ...language,
                    languageId: languageId || language.languageId,
                    languageName: languageName || language.languageName,
                    proficiencies: updatedProficiencies,
                };
            }
            return language;
        });

        // Update state with the modified developerLanguageProficiencies
        service.setState((prevState) => ({
            ...prevState,
            languages: {
                ...prevState.languages,
                developerLanguageProficiencies: updatedDeveloperLanguages,
            },
        }));
    };


    const addNewLanguage = () => {

        // Get the list of languageIds already selected in developerLanguageProficiencies
        const selectedLanguageIds = developerLanguageProficiencies.map((language) => language.languageId).filter(Boolean);

        // Update the languagesList to disable selected languages
        const updatedLanguagesList = languagesList.map((language) => ({
            ...language,
            disabled: selectedLanguageIds.includes(language.value), // Disable if the language is already selected
        }));

        const newLanguage = {
            renderId: `temp-${Math.random()}`,
            languageId: null,
            languageName: '',
            developerLanguageId: null,
            proficiencies: [],
        };

        service.setState((prevState) => ({
            ...prevState,
            languages: {
                ...prevState.languages,
                developerLanguageProficiencies: [
                    ...prevState.languages.developerLanguageProficiencies,
                    newLanguage,
                ],
                languagesList: updatedLanguagesList, // Update the state with the modified languagesList
            },
        }));
    };

    const deleteProficiency = async (renderId) => {
        const languageToDelete = developerLanguageProficiencies.find(
            (language) => language.renderId === renderId
        );

        if (!languageToDelete) return;

        const { languageId, developerLanguageId } = languageToDelete;

        if (languageId && developerLanguageId) {
            await removeDeveloperLanguage({ languageIds: [languageId] });
            await loadDeveloperLanguageProficiencies();
        } else {
            service.setState((prevState) => ({
                ...prevState,
                languages: {
                    ...prevState.languages,
                    developerLanguageProficiencies: prevState.languages.developerLanguageProficiencies.filter(
                        (language) => language.renderId !== renderId
                    ),
                },
            }));
        }
    };


    const handleSave = async (showSuccessAlert) => {
        try {
            const filteredProficiencies = getDeveloperLanguageProficiencies().filter(
                (language) => language.languageId !== null
            );
            if (filteredProficiencies.length === 0) {
                if (showSuccessAlert) {
                    message.info('Please select at least one language and its proficiency.');
                }
                return;
            }
            service.setState((prevState) => ({
                ...prevState,
                languages: {
                    ...prevState.languages,
                    developerLanguageProficiencies: [...filteredProficiencies],
                },
            }));
            service.setState({ loading: true });
            // API call after state update
            await saveDeveloperLanguageProficiencies(filteredProficiencies, showSuccessAlert);
            await loadDeveloperLanguageProficiencies();
            return true;
        } catch (error) {
            console.error('Error saving language proficiencies:', error);
        } finally {
            service.setState({ loading: false });
        }
    };

    return (
        <section className={customClass || ''}>
            <div className='languages-form'>
                <div className='instructions'>
                    <h2 className='title'>Select all the languages you speak, regardless of your skill level. For each language, specify your proficiency in speaking, writing, reading, and listening.</h2>
                    <ProficiencyLevels />
                </div>

                {(developerLanguageProficiencies || []).map(developerLanguageProficiency => (
                    <ProficiencyForm
                        key={developerLanguageProficiency.renderId}
                        developerLanguageProficiency={developerLanguageProficiency}
                        languagesList={languagesList}
                        proficiencyTypes={proficiencyTypes}
                        proficiencyLevels={proficiencyLevels}
                        updateProficiency={updateLanguageProficiency}
                        deleteProficiency={deleteProficiency}
                    />
                ))}

                <Button type='link' className='add-language' onClick={addNewLanguage} disabled={developerLanguageProficiencies.length >= 5}>
                    <AddLanguageIcon disabled={developerLanguageProficiencies.length >= 5} />
                    Add new language
                </Button>
                <div className='action-buttons'>
                    <Button onClick={() => onPanelSelect(null)}>Cancel</Button>
                    <Button type="primary" onClick={() => handleSave(true)}>Update information</Button>
                </div>
            </div>
        </section>
    );
}

const ProficiencyLevels = () => {
    const proficiencyLevels = [
        {
            id: 1,
            name: 'Beginner',
            description: 'You can understand basic words or phrases, but you are not yet comfortable holding conversations.',
        },
        {
            id: 2,
            name: 'Basic',
            description: 'You can participate in simple conversations and understand familiar topics, but with limited vocabulary or fluency.',
        },
        {
            id: 3,
            name: 'Intermediate',
            description: 'You can hold conversations, understand most situations, and discuss both personal and some professional topics.',
        },
        {
            id: 4,
            name: 'Advanced',
            description: 'You can communicate effectively on complex topics, including technical and professional subjects, with confidence.',
        },
        {
            id: 5,
            name: 'Native',
            description: 'You have full command of the language, with ease in any context, and are indistinguishable from a native speaker.',
        },
    ];

    return (
        <ul className='proficiency-scale'>
            {proficiencyLevels.map((level) => (
                <li className='item' key={level.id}>
                    <h3 className='name'>{level.name}</h3>
                    <p className='description'>{level.description}</p>
                </li>
            ))}
        </ul>
    )
};

const ProficiencyForm = ({
    developerLanguageProficiency,
    languagesList,
    proficiencyTypes,
    proficiencyLevels,
    updateProficiency,
    deleteProficiency
}) => {

    const handleLanguageChange = (value, { props }) => {
        updateProficiency(
            developerLanguageProficiency.renderId,
            null,
            null,
            props.value,
            props.children
        );
    };

    const handleProficiencyChange = (proficiencyTypeId, value) => {
        updateProficiency(
            developerLanguageProficiency.renderId,
            proficiencyTypeId,
            value,
            developerLanguageProficiency.languageId,
            developerLanguageProficiency.languageName
        );
    };

    const handleDelete = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();

        const languageToDelete = developerLanguageProficiency;

        Modal.confirm({
            className: 'delete-item-modal',
            title: (
                <div className="Delete-work-experience-item">
                    <h1>Delete Language</h1>
                    <div className="Delete-work-experience-item-details">
                        <span>
                            <b>{languageToDelete.languageName || 'New Language'}</b>
                        </span>
                    </div>
                </div>
            ),
            content: <p className="Delete-work-experience-item-p">Be sure you want to delete this Language, after deleting you won&apos;t be able to get it back.</p>,
            okText: 'Delete Language',
            okType: 'danger',
            cancelText: 'Cancel',
            iconType: null,
            width: 400,
            onOk: async () => {
                await deleteProficiency(developerLanguageProficiency.renderId);
            },
        });
    };

    const getDefaultProficiencyLevel = (developerLanguageProficiency, proficiencyTypeId) => {
        return developerLanguageProficiency.proficiencies.find(
            (proficiency) => {
                return proficiency.proficiencyTypeId === proficiencyTypeId
            }
        )?.proficiencyLevelId;
    }

    return (
        <>
            <div className='line'></div>
            <div className='proficiency-form'>
                <div className='language-details'>
                    <div className='language'>
                        <div className='form-field'>
                            <label>Language</label>
                            <Select
                                placeholder="Language"
                                mode="default"
                                value={developerLanguageProficiency.languageId}
                                showArrow
                                disabled={!!developerLanguageProficiency.languageId}
                                showSearch={true}
                                dropdownClassName={"turing-onboarding-new-ui-ux-dropdown"}
                                style={{ width: '100%' }}
                                filterOption={(input, option) =>
                                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                onSelect={handleLanguageChange}
                            >
                                {(languagesList || []).map((language) => (
                                    <Select.Option key={language.value} value={language.value} disabled={language.disabled}>
                                        {language.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                    <Button type='link' onClick={handleDelete}>
                        <span> <WorkExpDeleteIcon /></span>
                    </Button>
                </div>
                {developerLanguageProficiency.languageId &&
                    <div className='proficiency-details'>
                        {(proficiencyTypes || []).map(proficiencyType => (
                            <div className='form-field' key={proficiencyType.id}>
                                <label>{proficiencyType.name}</label>
                                <Select
                                    placeholder={`${proficiencyType.name} Proficiency`}
                                    mode="default"
                                    showArrow
                                    value={
                                        getDefaultProficiencyLevel(developerLanguageProficiency, proficiencyType.id)
                                    }
                                    onChange={(value) => handleProficiencyChange(proficiencyType.id, value)}
                                >
                                    {(proficiencyLevels || []).map(proficiencyLevels => (
                                        <Select.Option key={proficiencyLevels.value} value={proficiencyLevels.value}>{proficiencyLevels.label}</Select.Option>
                                    ))}

                                </Select>
                            </div>
                        ))}
                    </div>
                }
            </div>
        </>
    )
}

ProficiencyForm.propTypes = {
    developerLanguageProficiency: PropTypes.object.isRequired,
    languagesList: PropTypes.array.isRequired,
    proficiencyTypes: PropTypes.array.isRequired,
    proficiencyLevels: PropTypes.array.isRequired,
    updateProficiency: PropTypes.func.isRequired,
};

LanguagesForm.propTypes = {
    service: PropTypes.any,
    customClass: PropTypes.string
};

export default LanguagesForm;
